import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box"

import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty, isUndefined } from "lodash";

import exchangeRateService from "@plannel/services/data/exchange-rate-service"

import PeriodFilter from "@plannel/components/filter/PeriodFilter";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Dialog from "@plannel/components/Dialog"
import ExcelExportButton from "@plannel/components/ExcelExportButton"
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridDatePicker from "@plannel/components/GridDatePicker"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import { AddButton, RemoveButton, SaveButton, FilterButton } from "@plannel/components/ActionIconButton";

import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "../utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";

import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
const CURRENT_DATE = new ZDate();
const START_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() - 5, 1);
const END_DATE = new ZDate(CURRENT_DATE.getFullYear(), CURRENT_DATE.getMonth() + 1, 0);
const INIT_DATE = { start: START_DATE, end: END_DATE };

const { snackBarMessageColor } = ColorUtil;
const { getCurrencySymbol } = CodeUtil;
const ExchangeRate = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState?.currentPage ?? 1);
  const [gridPages, setGridPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState?.pageSize ?? pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const [period, setPeriod] = useState(reduxViewState?.start && reduxViewState?.end ? { start: reduxViewState.start, end: reduxViewState.end } : INIT_DATE);
  const searchCode = useRef(reduxViewState?.searchCode ?? "");
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const gridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);
  const [datas, setDatas] = useState([]);

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (period?.start && period?.end) {
      searchFilters.startDate = period.start;
      searchFilters.endDate = period.end;
    }

    if (searchCode.current) {
      searchFilters.baseCurrencyCd = searchCode.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }

    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveExchangeRate = () => {
    const params = getRequestParams();
    exchangeRateService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setDatas(results);
        setGridPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { ...period, searchCode: searchCode.current, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      })
  }

  const columnDefs = [
    {
      headerName: "baseCurrency", field: "baseCurrencyCd", width: 300, filterType: 'string'
    },
    {
      headerName: "baseDt", field: "baseDate", width: 120,
      type: ["defaultValueParser"], cellEditor: GridDatePicker, cellEditorParams: { dateType: "month", isFirstDay: true },
      valueFormatter: (data) => dateUtils.formatDate(data.value, "yyyy-MM"), filterType: 'date', filterUnit: 'month'
    },
    { headerName: "targetCurrency", field: "targetCurrencyCd", width: 300, filterType: 'string' },
    { headerName: "exchangeRate", field: "exchangeRate", type: "rightAligned", width: 200, filterType: 'number' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const insertRow = () => {
    const newRow = {
      id: null,
      baseCurrencyCd: null,
      targetCurrencyCd: null,
      exchangeRate: null
    };

    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const deleteExchangeRate = () => {
    const deleteIds = [];

    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        deleteIds.push(data.id)
      }
    );

    if (deleteIds.length > 0) {
      exchangeRateService.remove(deleteIds)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
        })
        .catch(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        })
        .then(() => {
          retrieveExchangeRate();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const isInvalid = datas => {
    for (const data of datas) {
      const { baseCurrencyCd, baseDate, targetCurrencyCd, exchangeRate } = data;
      const emptyValues = [];
      const notValidValues = [];

      if (!baseCurrencyCd) {
        emptyValues.push(t("baseCurrencyCd"));
      } else {
        if (isUndefined(getCurrencySymbol(baseCurrencyCd))) {
          notValidValues.push(baseCurrencyCd);
        }
      }
      if (!baseDate) {
        emptyValues.push(t("baseDt"));
      }
      if (!targetCurrencyCd) {
        emptyValues.push(t("targetCurrencyCd"));
      } else {
        if (isUndefined(getCurrencySymbol(targetCurrencyCd))) {
          notValidValues.push(targetCurrencyCd);
        }
      }
      if (!exchangeRate) {
        emptyValues.push(t("exchangeRate"));
      } else {
        if (!isFinite(exchangeRate)) {
          const errorMessage = `[${exchangeRate}] ${t("MSG_NotInNumericFormat")}`;
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: errorMessage });
          return true;
        }
      }

      if (!isEmpty(emptyValues)) {
        const values = emptyValues.join(",");
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: `[${values}]` }) });
        return true;
      }

      if (!isEmpty(notValidValues)) {
        const values = notValidValues.join(",");
        const errorMessage = `[${values}] ${t("MSG_InvalidValue")}`;
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: errorMessage });
        return true;
      }
    }

    return false;
  }

  const saveExchangeRate = () => {
    gridRef.current.api.stopEditing();

    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (isInvalid(allStateData)) {
      return;
    }

    if (allStateData.length > 0) {
      exchangeRateService.upsert(allStateData)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveExchangeRate();
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: "MSG_NoSaveData" });
    }
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveExchangeRate();
    } else if (action === "DELETE") {
      deleteExchangeRate();
    }
  };

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveExchangeRate();
    }
  }

  const onGridReady = (params) => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  useEffect(() => {
    if (gridLoading && period) {
      DataState.initialize(gridRef.current?.api);
      retrieveExchangeRate();
    }

    return () => {
      setDatas([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading && period) {
      initPageToRetrieve();
    }

    return () => {
      setDatas([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, advancedFilters]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <Box sx={{ position: "relative", display: "flex" }}>
          <PeriodFilter period={period} setPeriod={setPeriod} dateType="month" />
        </Box>
        <TextFieldSearch sx={{ width: 180 }} label={"baseCurrency"} searchCode={searchCode} retrieve={initPageToRetrieve} />
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={exchangeRateService} columnDefs={columnDefs} params={getRequestParams(false)} />
          <AddButton onClick={insertRow} />
          <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
          <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="exchange-rate-grid" className="ag-theme-balham grid-area" mb={1}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={datas}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              onGridReady={onGridReady}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
            />
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={columnDefs}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <PaginationContainer
        currentPage={currentPage} totalPages={gridPages}
        pageSize={pageSize} pageSizes={pageSizes}
        setCurrentPage={setCurrentPage} setPageSize={setPageSize}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
        disableButton={dialogInfo.disableButton}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(ExchangeRate);